import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = 'https://ufc-backend-nmib.onrender.com/api';

// Debounce helper — waits 300ms after the user stops typing before searching
function useDebounce(fn, delay) {
  const timer = React.useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

function SearchBox({ label, selected, onSelect, onClear, color }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fighters/search`, { params: { q } });
      setResults(res.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useDebounce(doSearch, 300);

  function handleChange(e) {
    setQuery(e.target.value);
    debouncedSearch(e.target.value);
  }

  function handleSelect(fighter) {
    onSelect(fighter);
    setQuery('');
    setResults([]);
  }

  if (selected) {
    return (
      <div>
        <div className="search-label" style={{ color }}>{label}</div>
        <div className="fighter-selected">
          <div>
            <div className="fighter-selected-name">{selected.full_name}</div>
            <div className="fighter-selected-meta">
              {selected.latest_weight_class} · {selected.wins}W {selected.losses}L {selected.draws}D
            </div>
          </div>
          <button className="btn-clear" onClick={onClear}>Change</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="search-label" style={{ color }}>{label}</div>
      <div className="search-box">
        <input
          type="text"
          placeholder="Search fighter name..."
          value={query}
          onChange={handleChange}
          autoComplete="off"
        />
        {(results.length > 0 || loading) && (
          <div className="search-results">
            {loading && (
              <div className="search-result-item" style={{ color: '#666' }}>
                Searching...
              </div>
            )}
            {results.map(f => (
              <div
                key={f.fighter_id}
                className="search-result-item"
                onClick={() => handleSelect(f)}
              >
                <span className="result-name">{f.full_name}</span>
                <span className="result-meta">
                  {f.latest_weight_class} · {f.wins}W {f.losses}L
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const [fighter1, setFighter1] = useState(null);
  const [fighter2, setFighter2] = useState(null);
  const navigate = useNavigate();

  function handleCompare() {
    if (!fighter1 || !fighter2) return;
    navigate(`/matchup?f1=${fighter1.fighter_id}&f2=${fighter2.fighter_id}`);
  }

  return (
    <>
      <div className="header">
        <div className="container">
          <h1>UFC <span>Fight</span> Predictor</h1>
        </div>
      </div>

      <div className="container">
        <div className="card">
          <h2>Select Two Fighters</h2>
          <div className="search-grid">
            <SearchBox
              label="Fighter 1"
              color="#e8372a"
              selected={fighter1}
              onSelect={setFighter1}
              onClear={() => setFighter1(null)}
            />
            <SearchBox
              label="Fighter 2"
              color="#2563eb"
              selected={fighter2}
              onSelect={setFighter2}
              onClear={() => setFighter2(null)}
            />
          </div>
          <button
            className="btn-compare"
            disabled={!fighter1 || !fighter2}
            onClick={handleCompare}
          >
            {fighter1 && fighter2
              ? `Compare ${fighter1.full_name} vs ${fighter2.full_name}`
              : 'Select both fighters to compare'}
          </button>
        </div>
      </div>
    </>
  );
}
